/**
 * Evaluates a segment rule tree over customers (SEG-01 to SEG-09, SEG-13).
 * Pure: the same data always gives the same result (SEG-16).
 */
import type { Customer, CustomerOrder, RuleCondition, RuleGroup, RuleNode } from '../../components/customers/data/types';
import { isGroup } from './customerSegmentRules';
import { isReachable, reachableChannels } from './customerConsent';

const DAY_MS = 86_400_000;

/** Which campaigns each customer was sent, and which of those they redeemed, keyed by customer id. */
export interface CampaignHistory {
  received: Record<string, string[]>;
  redeemed: Record<string, string[]>;
}

export interface EvaluateContext {
  now: Date;
  /** Needed only by the "received campaign" and "redeemed campaign" rules. */
  history?: CampaignHistory;
}

interface InternalContext extends EvaluateContext {
  customers: Customer[];
  topSpenderCache: Map<string, Set<string>>;
}

/** SEG-13 — who matches, and how many of them each channel can reach. */
export interface AudienceCounts {
  matching: number;
  sms: number;
  email: number;
  push: number;
  /** LOY-06 — matching customers who are not loyalty members. */
  nonMembers: number;
  /** Matching customers left out of every campaign because their risk flag is Blocked. */
  blocked: number;
}

export const EMPTY_AUDIENCE: AudienceCounts = { matching: 0, sms: 0, email: 0, push: 0, nonMembers: 0, blocked: 0 };

const toCents = (amount: number) => Math.round(amount * 100);

export function daysSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  return Math.floor((now.getTime() - new Date(iso).getTime()) / DAY_MS);
}

/** Birthday month as 1–12, or null when no birthday is on file. */
export function birthdayMonth(customer: Pick<Customer, 'birthday'>): number | null {
  const month = Number((customer.birthday ?? '').slice(5, 7));
  return month >= 1 && month <= 12 ? month : null;
}

/** A blocked customer is never sent a campaign or given a campaign reward. */
export function isBlocked(customer: Pick<Customer, 'riskStatus'>): boolean {
  return customer.riskStatus === 'blocked';
}

function ordersInPeriod(customer: Customer, periodDays: number | undefined, now: Date): CustomerOrder[] {
  if (!periodDays) return customer.orders;
  const cutoff = now.getTime() - periodDays * DAY_MS;
  return customer.orders.filter((o) => new Date(o.at).getTime() >= cutoff);
}

const spendCents = (orders: CustomerOrder[]) => orders.reduce((sum, o) => sum + toCents(o.total), 0);

function compareNumber(actual: number | null, cond: RuleCondition): boolean {
  if (actual === null) return false;
  const v = cond.value;
  switch (cond.operator) {
    case 'lt': return actual < (v as number);
    case 'gt': return actual > (v as number);
    case 'gte': return actual >= (v as number);
    case 'lte': return actual <= (v as number);
    case 'eq': return actual === (v as number);
    case 'between': {
      const [from, to] = v as [number, number];
      return actual >= from && actual <= to;
    }
    default: return false;
  }
}

function compareList(actual: string[], cond: RuleCondition): boolean {
  const wanted = cond.value as string[];
  const any = wanted.some((w) => actual.includes(w));
  return cond.operator === 'not_in' ? !any : any;
}

const lower = (values: string[]) => values.map((v) => v.trim().toLowerCase());

/** SEG-05 — customers in the top N% by spend within the period. */
function topSpenders(percent: number, periodDays: number | undefined, ctx: InternalContext): Set<string> {
  const cacheKey = `${percent}|${periodDays ?? 'all'}`;
  const cached = ctx.topSpenderCache.get(cacheKey);
  if (cached) return cached;
  const ranked = ctx.customers
    .map((c) => ({ id: c.id, spend: periodDays ? spendCents(ordersInPeriod(c, periodDays, ctx.now)) : toCents(c.lifetimeSpend) }))
    .filter((r) => r.spend > 0)
    .sort((a, b) => b.spend - a.spend);
  const take = Math.ceil((ranked.length * percent) / 100);
  const set = new Set(ranked.slice(0, take).map((r) => r.id));
  ctx.topSpenderCache.set(cacheKey, set);
  return set;
}

function matchCondition(cond: RuleCondition, c: Customer, ctx: InternalContext): boolean {
  const orders = () => ordersInPeriod(c, cond.periodDays, ctx.now);
  const loyalty = c.loyaltyStatus ?? null;
  switch (cond.field) {
    case 'last_order_days_ago': return compareNumber(daysSince(c.lastVisitAt, ctx.now), cond);
    case 'first_order_days_ago': return compareNumber(daysSince(c.firstVisitAt ?? c.orders[0]?.at, ctx.now), cond);
    case 'order_count': return compareNumber(cond.periodDays ? orders().length : c.visitCount, cond);
    case 'total_spend_cents': return compareNumber(cond.periodDays ? spendCents(orders()) : toCents(c.lifetimeSpend), cond);
    case 'avg_order_value_cents': return compareNumber(c.visitCount > 0 ? toCents(c.lifetimeSpend / c.visitCount) : null, cond);
    case 'ordered_item_id': return compareList(orders().flatMap((o) => o.itemIds), cond);
    case 'ordered_category_id': return compareList(orders().flatMap((o) => o.categoryIds), cond);
    case 'order_channel': return compareList(c.orders.map((o) => o.channel), cond);
    case 'outlet_id': return compareList(c.orders.map((o) => o.outletId), cond);
    case 'daypart': return compareList(c.orders.map((o) => o.daypart), cond);
    case 'loyalty_member': return Boolean(loyalty) === cond.value;
    case 'loyalty_tier': return compareList(loyalty ? [`tier_${loyalty.tier.trim().toLowerCase()}`] : [], cond);
    case 'points_balance': return Boolean(loyalty) && compareNumber(loyalty?.points ?? 0, cond);
    case 'has_unredeemed_reward': return Boolean(loyalty?.hasUnredeemedReward) === cond.value;
    case 'points_expiring_within_days': return compareNumber(loyalty?.pointsExpiringInDays ?? null, cond);
    case 'birthday_month': return birthdayMonth(c) === cond.value;
    case 'has_app': return c.appInstalled === cond.value;
    case 'tag': return compareList(lower(c.tags), { ...cond, value: lower(cond.value as string[]) });
    case 'group_id': return compareList(c.groupIds, cond);
    case 'risk_status': return compareList([c.riskStatus], cond);
    case 'lifecycle_state': return compareList([c.lifecycleState], cond);
    case 'received_campaign_id': return compareList(ctx.history?.received[c.id] ?? [], cond);
    case 'redeemed_campaign_id': return compareList(ctx.history?.redeemed[c.id] ?? [], cond);
    case 'spend_top_percent': return topSpenders(cond.value as number, cond.periodDays, ctx).has(c.id);
    case 'birthday_this_month': return (birthdayMonth(c) === ctx.now.getMonth() + 1) === cond.value;
    case 'reachable': return (reachableChannels(c).length > 0) === cond.value;
    default: return false;
  }
}

function matchNode(node: RuleNode, c: Customer, ctx: InternalContext): boolean {
  if (!isGroup(node)) return matchCondition(node, c, ctx);
  if (node.children.length === 0) return false;
  return node.op === 'and'
    ? node.children.every((child) => matchNode(child, c, ctx))
    : node.children.some((child) => matchNode(child, c, ctx));
}

/**
 * Customers matching the rules, blocked customers included; `splitBlocked` gives who a campaign reaches.
 * `outletIds` narrows to customers whose preferred or last-ordered outlet is listed; empty means all.
 */
export function evaluateSegment(
  rules: RuleGroup,
  customers: Customer[],
  context: EvaluateContext,
  outletIds: string[] = [],
): Customer[] {
  const ctx: InternalContext = { ...context, customers, topSpenderCache: new Map() };
  return customers.filter((c) => {
    if (outletIds.length > 0) {
      const lastOutlet = c.orders.length > 0 ? c.orders[c.orders.length - 1].outletId : null;
      const inOutlets = (c.preferredOutletId && outletIds.includes(c.preferredOutletId)) || (lastOutlet && outletIds.includes(lastOutlet));
      if (!inOutlets) return false;
    }
    return matchNode(rules, c, ctx);
  });
}

/** Separates the customers a campaign can go to from the matching customers whose risk flag is Blocked. */
export function splitBlocked(matched: Customer[]): { audience: Customer[]; blocked: Customer[] } {
  return { audience: matched.filter((c) => !isBlocked(c)), blocked: matched.filter(isBlocked) };
}

/** SEG-13 — matching, and reachable by SMS, email and push; LOY-06 — non-members. Blocked customers count as unreachable. */
export function countAudience(matched: Customer[]): AudienceCounts {
  const { audience, blocked } = splitBlocked(matched);
  const counts: AudienceCounts = { ...EMPTY_AUDIENCE, matching: matched.length, blocked: blocked.length };
  for (const c of audience) {
    if (isReachable(c, 'sms')) counts.sms += 1;
    if (isReachable(c, 'email')) counts.email += 1;
    if (isReachable(c, 'push')) counts.push += 1;
    if (!c.loyaltyStatus) counts.nonMembers += 1;
  }
  return counts;
}
